import { useMemo } from 'react'
import { StateDot, StateBadge } from '../ui/State'
import {
  DECLARATION_STATUSES,
  DECLARATION_STATUS_LABEL,
  DECLARATION_STATUS_TONE,
  STATUS_ATTENTION,
  countByStatus,
} from '../../lib/statusModel'
import type { DeclarationStatus } from '../../lib/statusModel'
import type { FlightDeclaration } from '../../types'

/* ============================================================================
 * БЕЙДЖИ-СЧЁТЧИКИ ПО СТАТУСАМ ЗАЯВОК (задача №7)
 *
 * Диспетчер должен видеть состояние КОНТУРА ЗАЯВОК на верхнем уровне, а не
 * листать список. Нулевые статусы показываются приглушённо: пустая полоса —
 * это тоже информация («всё согласовано»), а не «панель сломалась».
 * ========================================================================= */

export function normalizeDeclarations(
  list: FlightDeclaration[],
): Array<{ decl: FlightDeclaration; status: DeclarationStatus; raw?: string }> {
  return list.map((d) => ({ decl: d, status: d.status ?? 'pending', raw: d.status_raw }))
}

/** Сводная полоса счётчиков. */
export function StatusCounters({ declarations }: { declarations: FlightDeclaration[] }) {
  const rows = useMemo(() => {
    const norm = normalizeDeclarations(declarations)
    const counts = countByStatus(norm)
    return DECLARATION_STATUSES.map((s) => ({
      status: s,
      count: counts[s] ?? 0,
      label: DECLARATION_STATUS_LABEL[s],
      tone: DECLARATION_STATUS_TONE[s],
      attention: (STATUS_ATTENTION as readonly string[]).includes(s),
    }))
  }, [declarations])

  const total = rows.reduce((a, r) => a + r.count, 0)

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      aria-label="Счётчики заявок по статусам"
    >
      {rows.map((r) => (
        <span
          key={r.status}
          className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${
            r.count === 0
              ? 'border-line/40 text-ink-muted/50'
              : r.attention
                ? 'border-critical/60 text-critical'
                : 'border-line/70 text-ink-muted'
          }`}
          title={`${r.label}: ${r.count}`}
        >
          <StateDot state={r.count === 0 ? 'pending' : r.tone} pulsing={r.attention && r.count > 0} />
          <span>{r.label}</span>
          <b className="ut-metric">{r.count}</b>
        </span>
      ))}
      <span className="text-[10px] text-ink-muted">
        всего: <b className="ut-metric">{total}</b>
      </span>
    </div>
  )
}

/** Счётчик одного статуса — для бейджа на вкладке. */
export function StatusBadgeCount({
  status,
  count,
}: {
  status: DeclarationStatus
  count: number
}) {
  if (count === 0) return null
  return (
    <StateBadge state={DECLARATION_STATUS_TONE[status]}>
      {count}
    </StateBadge>
  )
}
